import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";

const initialState = {
  users: [],
  adminUsers: [],
  adminUsersTotals: {
    totalUsers: 0,
    totalAdmins: 0,
    totalRegularUsers: 0,
    totalBalance: 0,
  },
  adminUsersPagination: {
    page: 1,
    limit: 100,
    totalCount: 0,
    totalPages: 1,
  },
  allTransactions: [],
  adminTransactions: [],
  adminTransactionsTotals: null,
  adminTransactionsPagination: {
    page: 1,
    limit: 100,
    totalCount: 0,
    totalPages: 1,
  },
  stats: null,
  selectedUser: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchUsers = createAsyncThunk(
  "admin/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getUsers();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchAdminUsers = createAsyncThunk(
  "admin/fetchAdminUsers",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getUsers(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchUserById = createAsyncThunk(
  "admin/fetchUserById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateUser = createAsyncThunk(
  "admin/updateUser",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUser(id, data);
      toast.success("User updated successfully! ✨");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update user");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const deleteUser = createAsyncThunk(
  "admin/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      await adminService.deleteUser(id);
      toast.success("User deleted successfully");
      return id;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete user");
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchAllTransactions = createAsyncThunk(
  "admin/fetchAllTransactions",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAllTransactions(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchAdminTransactions = createAsyncThunk(
  "admin/fetchAdminTransactions",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAllTransactions(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchDashboardStats = createAsyncThunk(
  "admin/fetchDashboardStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getDashboardStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.users;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch User by ID
      .addCase(fetchUserById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update User
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(
          (u) => u._id === action.payload.user.id,
        );
        if (index !== -1) {
          state.users[index] = {
            ...state.users[index],
            ...action.payload.user,
          };
        }
      })
      // Delete User
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((u) => u._id !== action.payload);
      })
      // Fetch All Transactions
      .addCase(fetchAllTransactions.fulfilled, (state, action) => {
        state.allTransactions = action.payload.transactions;
      })
      // Fetch Admin Users with pagination
      .addCase(fetchAdminUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminUsers = action.payload.users;
        state.adminUsersTotals = action.payload.totals;
        state.adminUsersPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Admin Transactions with pagination
      .addCase(fetchAdminTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminTransactions = action.payload.transactions;
        state.adminTransactionsTotals = action.payload.totals;
        state.adminTransactionsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchAdminTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Dashboard Stats
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.stats = action.payload.stats;
      });
  },
});

export const { clearSelectedUser } = adminSlice.actions;
export default adminSlice.reducer;
